import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  IconButton,
  Stack,
  Tooltip,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ThumbUp, 
  Edit,
  Delete,
  Add,
} from '@mui/icons-material';
import { GridColDef, GridFilterModel, GridOverlay } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { normalizeFilterModel, sameModel, stringContainsOnly } from '../../../utils/adminDashboardUtils';
import fetchPartnerResources, {
  deletePartnerResource,
  fetchResourceCategories,
  patchPartnerResource,
  postPartnerResource,
  RESOURCE_CATEGORY_TITLES,
} from '../../../services/partnerResources';

import StandardDialog, { StandardDialogActions } from '../../Modals/StandardDialog';
import { AsyncSaveButton } from '../../../components/buttons/AsyncSaveButton';
import BaseToolbar from '../../Datagrid/BaseToolbar';
import moment from 'moment';
import { PartnerResource, PartnerResourceCategory } from '../../../interfaces/partnerResources.interface';

const KeyTermsAndDefinitionsAdmin = () => {
  const [rows, setRows] = useState<PartnerResource[]>([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [filterModelDraft, setFilterModelDraft] = useState<GridFilterModel>({ items: [] });
  const [filterModelApplied, setFilterModelApplied] = useState<GridFilterModel>({ items: [] });
  const [editingRow, setEditingRow] = useState<PartnerResource | null>(null);
  const [editTerm, setEditTerm] = useState('');
  const [editDefinition, setEditDefinition] = useState('');
  const [deletingRow, setDeletingRow] = useState<PartnerResource | null>(null);
  const [keyTermsCategoryId, setKeyTermsCategoryId] = useState<number | null>(null);
  const [newTerm, setNewTerm] = useState('');
  const [newDefinition, setNewDefinition] = useState('');
  const [addTermError, setAddTermError] = useState('');
  const [editKeyTermError, setEditKeyTermError] = useState('');

  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [loading, setLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [newKeyTermModalOpen, setNewKeyTermModalOpen] = useState(false);
  const [showKeyTermSaved, setShowKeyTermSaved] = useState(false);

  function CustomToolbar(props: { openInviteAdminModal: () => void }) {
    const { openInviteAdminModal, ...rest } = props;

    return (
      <BaseToolbar
      {...rest}
      csvOptions={{ fileName: `key_terms_and_definitions_${moment().format('MM_DD_YYYY')}` }}
      >
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<Add fontSize="small" />}
            onClick={() => setNewKeyTermModalOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add Key Term
          </Button>
        </Box>
      </BaseToolbar>
    );
  }

  const fetchKeyTermsAndDefinitions = useCallback(async (categoryId: number) => {
    setLoading(true);
    try {
      const { count, results } = await fetchPartnerResources({
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
        ordering: sortModel.length > 0 ? `${sortModel[0].sort === 'desc' ? '-' : ''}${sortModel[0].field}` : undefined,
        filterModel: filterModelApplied,
        categoryId,
      });

      setRows(results);
      setRowCount(count);
    } catch (error) {
      console.error('Error fetching key terms and definitions:', error);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, sortModel, filterModelApplied]);

  const handleFetchResourceCategories = useCallback(async () => {
    try {
      const { results } = await fetchResourceCategories();

      const keyTermsId = results.find((cat: PartnerResourceCategory) => cat.title === RESOURCE_CATEGORY_TITLES.KEY_TERMS)?.id;
      if (keyTermsId) {
        setKeyTermsCategoryId(keyTermsId);
        fetchKeyTermsAndDefinitions(keyTermsId);
      } else {
        setKeyTermsCategoryId(null);
        console.warn('KEY TERMS & DEFINITIONS category not found. Cannot fetch key terms.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching resource categories:', error);
    }
  }, []);

  const handleOpenEdit = useCallback((row: PartnerResource) => {
    setEditingRow(row);
    setEditTerm(row.title ?? '');
    setEditDefinition(row.description ?? '');
    setIsEditOpen(true);
  }, []);

  const handleCloseAdd = useCallback(() => {
    if (isAdding) return;

    setNewTerm('');
    setNewDefinition('');
    setAddTermError('');
    setNewKeyTermModalOpen(false);
  }, [isAdding]);

  const handleCloseEdit = useCallback(() => {
    if (isSaving) return;

    setIsEditOpen(false);
    setEditingRow(null);
    setEditKeyTermError('');
    setEditTerm('');
    setEditDefinition('');
  }, [isSaving]);

  const handleOpenDelete = useCallback((row: PartnerResource) => {
    setDeletingRow(row);
    setIsDeleteOpen(true);
  }, []);

  const handleCloseDelete = useCallback(() => {
    if (isDeleting) return;

    setIsDeleteOpen(false);
    setDeletingRow(null);
  }, [isDeleting]);

  const handleDeleteConfirmed = useCallback(async () => {
    if (!deletingRow) return;

    setIsDeleting(true);
    try {
      const success = await deletePartnerResource(Number(deletingRow.id));
      if (!success) {
        console.error('Failed to delete key term. Please try again.');
        return;
      }

      setRows((prev) => prev.filter((r) => r.id !== deletingRow.id));
      setRowCount((prev) => Math.max(0, prev - 1));
      handleCloseDelete();
      setShowKeyTermSaved(true);
    } catch (error) {
      console.error('Error deleting key term:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [deletingRow, handleCloseDelete]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingRow) return;

    if (!editTerm.trim() || !editDefinition.trim()) {
      setEditKeyTermError('Both term and definition are required.');
      return;
    }

    setIsSaving(true);
    try {
      setEditKeyTermError('');
      const updatedKeyTerm = await patchPartnerResource(Number(editingRow.id), {
        title: editTerm,
        description: editDefinition,
      });

      setRows((prev) =>
        prev.map((r) =>
          r.id === editingRow.id
            ? {
              ...r,
              ...updatedKeyTerm,
            }
            : r,
        ),
      );

      handleCloseEdit();
      setShowKeyTermSaved(true);
    } catch (error) {
      console.error('Error updating key term:', error);
    } finally {
      setIsSaving(false);
    }
  }, [editingRow, editTerm, editDefinition, handleCloseEdit]);

  const handleAddNewKeyTerm = useCallback(async () => {
    if (!newTerm.trim() || !newDefinition.trim()) {
      setAddTermError('Both term and definition are required.');
      return;
    }

    setAddTermError('');
    setIsAdding(true);
    try {
      if (!keyTermsCategoryId) {
        console.error('No category found for KEY TERMS & DEFINITIONS. Cannot add new key term.');
        return;
      }

      if (!rows[0]?.author) {
        console.error('No author information available from existing key terms. Cannot add new key term.');
        return;
      }

      const newKeyTerm = await postPartnerResource({
        category: keyTermsCategoryId,
        author: rows[0].author.id,
        title: newTerm,
        description: newDefinition,
        sub_description: newDefinition,
      });

      setRows((prev) => [newKeyTerm, ...prev]);
      setRowCount((prev) => prev + 1);

      setNewTerm('');
      setNewDefinition('');
      setNewKeyTermModalOpen(false);
      setShowKeyTermSaved(true);
    } catch (error) {
      console.error('Error adding new key term:', error);
    } finally {
      setIsAdding(false);
    }
  }, [newTerm, newDefinition, keyTermsCategoryId]);

  useEffect(() => {
    handleFetchResourceCategories();
  }, [handleFetchResourceCategories]);

  useEffect(() => {
    if (rows.length > 0 && rows[0].category?.id) {
      fetchKeyTermsAndDefinitions(Number(rows[0].category.id));
    }
  }, [paginationModel.page, paginationModel.pageSize, sortModel, filterModelApplied, fetchKeyTermsAndDefinitions]);

  const columns: GridColDef<PartnerResource>[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Term',
        filterOperators: stringContainsOnly,
        width: 220,
        valueGetter: (params) => params.row.title,
        valueFormatter: (params) => params.value || 'Unknown',
      },
      {
        field: 'description',
        headerName: 'Definition',
        filterOperators: stringContainsOnly,
        width: 520,
        valueGetter: (params) => params.row.description,
        valueFormatter: (params) => params.value || 'Unknown',
      },
      {
        field: 'updated_at',
        headerName: 'Updated At',
        filterOperators: stringContainsOnly,
        width: 180,
        valueGetter: (params) => {
          return params.row.updatedAt ? moment(params.row.updatedAt).format('M/D/YYYY') : 'Unknown';
        },
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 110,
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ width: '100%' }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEdit(params.row);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDelete(params.row);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [handleOpenDelete, handleOpenEdit],
  );

  return (
    <div>
      <Snackbar
        open={showKeyTermSaved}
        autoHideDuration={4000}
        onClose={() => setShowKeyTermSaved(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowKeyTermSaved(false)}
          icon={<ThumbUp fontSize="inherit" />}
          severity="success"
          sx={{ width: '100%' }}
        >
          Key Term saved successfully
        </Alert>
      </Snackbar>
      <Card sx={{ padding: '2em' }}>
        <DataGridPro
          loading={loading}
          style={{ height: 400, width: '100%' }}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id || ''}
          pagination
          paginationMode="server"
          filterMode="server"
          sortingMode="server"
          rowCount={rowCount}
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          onSortModelChange={(model) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            setSortModel(model);
          }}
          onPageChange={(newPage: number) => {
            setPaginationModel((prev) => ({ ...prev, page: newPage }));
          }}
          onPageSizeChange={(newPageSize: number) => {
            setPaginationModel((prev) => ({ ...prev, pageSize: newPageSize, page: 0 }));
          }}
          onFilterModelChange={(model) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            const normalized = normalizeFilterModel(model);
            if (!sameModel(normalized, filterModelApplied)) {
              setFilterModelApplied(normalized);
            }
          }}
          rowsPerPageOptions={[25, 50, 100]}
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'pre-line',
              lineHeight: '1.2',
              textAlign: 'left',
            },
            '& .MuiDataGrid-pinnedColumns': {
              boxShadow: 'none',
            },
            '& .MuiDataGrid-pinnedColumnHeaders': {
              boxShadow: 'none',
            },
          }}
          components={{
            Toolbar: CustomToolbar,
            LoadingOverlay: () => (
              <GridOverlay>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                  <CircularProgress />
                </div>
              </GridOverlay>
            ),
          }}
          componentsProps={{
            toolbar: {
              openFilterModal: () => {},
              filterModelDraft,
              setFilterModelDraft,
              handleFetchVerificationData: () => {},
            },
          }}
          initialState={{
            pinnedColumns: { right: ['actions'] },
          }}
        />
        {/* New Key Term Modal */}
        <StandardDialog
          title="Add Key Term"
          subtitle="Create a new key term and definition to be displayed in the TPN Resource Library."
          handleClose={handleCloseAdd}
          isOpen={newKeyTermModalOpen}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Term" value={newTerm} onChange={(e) => setNewTerm(e.target.value)} fullWidth />
            <TextField
              label="Definition"
              value={newDefinition}
              onChange={(e) => setNewDefinition(e.target.value)}
              fullWidth
              multiline
              minRows={5}
            />
            {addTermError && (
              <Alert severity="error" onClose={() => setAddTermError('')}>
                {addTermError}
              </Alert>
            )}
          </Box>
          <StandardDialogActions>
            <Button onClick={handleCloseAdd} disabled={isAdding}>
              Cancel
            </Button>
            <AsyncSaveButton
              onClick={handleAddNewKeyTerm}
              isSaving={isAdding}
              label="Add"
            />
          </StandardDialogActions>
        </StandardDialog>

        {/* Edit Modal */}
        <StandardDialog isOpen={isEditOpen} title="Edit Definition" handleClose={handleCloseEdit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Term"
              value={editTerm}
              onChange={(e) => setEditTerm(e.target.value)}
              fullWidth
              disabled={isSaving}
            />

            <TextField
              label="Definition"
              value={editDefinition}
              onChange={(e) => setEditDefinition(e.target.value)}
              fullWidth
              multiline
              minRows={5}
              disabled={isSaving}
            />

            {editKeyTermError && (
              <Alert severity="error" onClose={() => setEditKeyTermError('')}>
                {editKeyTermError}
              </Alert>
            )}
          </Box>
          <StandardDialogActions>
            <Button onClick={handleCloseEdit} disabled={isSaving}>
              Cancel
            </Button>
            <AsyncSaveButton
              onClick={handleSaveEdit}
              isSaving={isSaving}
              label="Save"
            />
          </StandardDialogActions>
        </StandardDialog>

        {/* Delete Confirmation Modal */}
        <StandardDialog
          isOpen={isDeleteOpen}
          title="Delete Key Term?"
          handleClose={handleCloseDelete}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box>
              Are you sure you want to delete{' '}
              <strong>{deletingRow?.title || 'this key term'}</strong>? This action cannot be undone.
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button onClick={handleCloseDelete} disabled={isDeleting}>
                Cancel
              </Button>
              <AsyncSaveButton
                onClick={handleDeleteConfirmed}
                isSaving={isDeleting}
                label="Delete"
              />
            </Box>
          </Box>
        </StandardDialog>
      </Card>
    </div>
  );
};

export default KeyTermsAndDefinitionsAdmin;
